import { Suspense, useEffect, useMemo, useState } from "react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { I18nextProvider } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { QueryClientProvider } from "@tanstack/react-query";
import { Button } from "semantic-ui-react";
import { ThemeProvider } from "styled-components";

import { localStorageIsSupported } from "simplydo/core/helpers";
import { i18n, setInterpolationDictionary } from "simplydo/core/i18n";
import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";
import { SizeProvider, useTheme } from "simplydo/react/hooks/useTheme";

import actions from "actions";
import api, { websocketApi } from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useDocumentLang } from "utils/useDocumentLang";
import util from "utils/utils";

import MessageMoles from "components/lib/Messaging/MessageMolesContainer";

import { serviceWorkerBridge } from "../serviceWorkerBridge";
import AppOnboarding from "./AppOnboarding";
import AppStyling from "./AppStyling";
import EventSubscriptions from "./EventSubscriptions";
import SuspenseLoading from "./app/SuspenseLoading";
import Toaster from "./app/Toaster";
import { useChatwoot } from "./app/useChatwoot";
import GoogleHelper from "./lib/GoogleHelper";
import { AutoBusinessProfileModal } from "./lib/IdeaBusinessProfileModal/AutoBusinessProfileModal";
import { MountPoint } from "./lib/Modal/MountPoint";

// Forcefully disconnect the websocket when the user leaves the page
window.addEventListener("beforeunload", () => {
  websocketApi.disconnect();
});

const ReloadAppWidget = () => (
  <div>
    <p style={{ fontWeight: "bold", marginBottom: 5 }}>
      A new version of {util.appName()} is available
    </p>
    <p style={{ fontSize: 17 }}>
      To load the most recent version simply refresh the page. Please make sure
      you have do not have any unsaved changes.
    </p>
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Button
        primary
        icon="refresh"
        onClick={() => {
          if (util.localStorageIsSupported()) {
            localStorage.setItem("lastPromptHash", api.webVersion);
          }
          window.location.reload();
        }}
        content="Refresh"
      />
    </div>
  </div>
);

const WebThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const organisation = useAppSelector((state) => {
    const urlOrganisation = state.organisations?.urlOrganisation;
    return state?.user?.ownerOrganisation || urlOrganisation || {};
  });
  const theme = useTheme({ organisation });
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

function App() {
  const user = useAppSelector((state) => state.user);
  useDocumentLang();
  const dispatch = useAppDispatch();

  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const selectedMessageThreads = useAppSelector(
    (state) => state.messages.selectedMessageThreads,
  );
  const clearUtm = useAppSelector((state) => state.auth.clearUtm);

  const navigate = useNavigate();
  const location = useLocation();
  const ownerOrganisation = user?.ownerOrganisation || urlOrganisation;
  const [params, setParams] = useSearchParams();
  const utmLinkReferrer = params.get("utm-link");
  const utmChallengeReferrer = params.get("utm-challenge");
  const appName = useMemo(
    () => ownerOrganisation?.appName || util.appName(),
    [ownerOrganisation?.appName],
  );
  const [promptReload, setPromptReload] = useState(true);
  const innoIntelEnabled = useMemo(
    () =>
      util.organisationFeaturesEnabled(user, ["innovationIntelligence"]) &&
      util.hasPermission(
        user,
        "org.innovationIntelligence",
        user.ownerOrganisation._id,
      ),
    [user],
  );

  // We track the referrer of users in local storage to allow for better analytics tracking of where users are coming from. We have a block list of referrers that we don't want to track as they are either not useful for analytics or could cause issues with local storage (eg if the referrer is another tab of the app or ms login)
  useEffect(() => {
    const referrer_block_list = ["simplydo", "login.microsoftonline.com"];
    const referrer = document?.referrer;
    if (
      localStorageIsSupported() &&
      referrer &&
      !referrer_block_list.some((blocked) => referrer.includes(blocked)) &&
      !referrer.includes(window.location.origin)
    ) {
      localStorage.setItem("SimplyDo-Referrer", referrer);
    }
  }, []);

  // Initialise Chatwoot plugin for support chat
  useChatwoot();

  // Set up service worker bridge with navigation actions
  useEffect(() => {
    serviceWorkerBridge.navigator = { push: navigate };
  }, [navigate]);

  useEffect(() => {
    if (!user && urlOrganisation && urlOrganisation?.terminology) {
      setInterpolationDictionary(urlOrganisation.terminology);
    }
  }, [user, urlOrganisation]);

  // UTM is a value associated with a custom URL. We attach these to our sdi.click links, to allow tracking analytics. We can then attribute sign-ups, clicks and views to specific sdi.click links and track timing
  useEffect(() => {
    if (utmLinkReferrer) {
      dispatch(actions.auth.setUtmLink(utmLinkReferrer));
    }
    if (utmChallengeReferrer) {
      dispatch(actions.auth.setUtmChallenge(utmChallengeReferrer));
    }
  }, [utmLinkReferrer, utmChallengeReferrer, dispatch]);

  // Clear utm's after a referral has been tracked
  // Used to clear utms after login, resume session and register
  useEffect(() => {
    if (clearUtm) {
      params.delete("utm-challenge");
      params.delete("utm-link");
      setParams(params);
      dispatch(actions.auth.setClearUtm(false));
    }
  }, [params, setParams, dispatch, clearUtm]);

  // Check for new version of the app, prompt the user to refresh if one is available
  useEffect(() => {
    if (!api?.webVersion) {
      return;
    }

    if (util.localStorageIsSupported()) {
      const lastHash = localStorage.getItem("lastPromptHash");
      if (lastHash === api.webVersion) {
        setPromptReload(false);
        return;
      }
    }

    if (
      import.meta.env.VITE_STAGE !== "Dev" &&
      import.meta.env.VITE_GIT_SHA !== api.webVersion &&
      promptReload
    ) {
      setPromptReload(false);
      toast(<ReloadAppWidget />, {
        duration: 15000,
        style: { minWidth: "600px" },
      });
    }
  }, [promptReload]);

  /*
    Track page views when the page changes, and scroll to top if moving to challenge/idea pages
  */
  useEffect(() => {
    const regExr = new RegExp(/\/challenges|ideas\/([A-z0-9]+)\w+/g);
    if (!regExr.test(window.location.pathname)) {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <ErrorBoundary pathName={location.pathname}>
      <I18nextProvider i18n={i18n} defaultNS={"translation"}>
        <QueryClientProvider client={api.queryClient}>
          <SizeProvider>
            <WebThemeProvider>
              <AppStyling>
                <DndProvider backend={HTML5Backend}>
                  <EventSubscriptions />
                  <Helmet
                    defaultTitle={appName}
                    titleTemplate={`%s | ${appName}`}
                  >
                    {import.meta.env.VITE_CSP && (
                      <meta
                        httpEquiv="Content-Security-Policy"
                        content={`${import.meta.env.VITE_CSP}`}
                      />
                    )}
                    <link
                      rel="shortcut icon"
                      type="image/png"
                      href={util.orgFavicon(ownerOrganisation)}
                    />
                  </Helmet>
                  <Suspense fallback={<SuspenseLoading />}>
                    <AppOnboarding />
                  </Suspense>
                  <MessageMoles
                    selectedMessageThreads={selectedMessageThreads}
                  />
                  <Toaster />
                  {innoIntelEnabled && (
                    <GoogleHelper
                      displayOnPages={["^/innovationintelligence"]}
                      containerStyle={{ bottom: 20, right: 20 }}
                    />
                  )}
                  <AutoBusinessProfileModal />
                  <MountPoint />

                  <div id="semantic-modal-mount-node" />
                </DndProvider>
              </AppStyling>
            </WebThemeProvider>
          </SizeProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
